/**
 * Admin CMS API client — wraps the /api/admin/{tools,articles,…} endpoints.
 *
 * Every request sends the admin_session cookie via `credentials:"include"`.
 * All helpers return parsed JSON and throw an error (with `.status` +
 * `.fieldErrors` when 422) so admin UIs can render inline validation.
 */

// API calls use relative `/api/...` paths. See ContentProvider.jsx.

async function handle(res) {
  let data = null;
  try { data = await res.json(); } catch { /* non-JSON ok */ }
  if (!res.ok) {
    const err = new Error((data && (data.detail?.message || data.detail || data.message)) || `HTTP ${res.status}`);
    err.status = res.status;
    if (res.status === 422 && data?.detail?.field_errors) {
      err.fieldErrors = data.detail.field_errors;
    }
    // Pydantic-style errors ([{loc, msg}]) — flatten to a field map.
    if (res.status === 422 && Array.isArray(data?.detail)) {
      const fieldErrors = {};
      data.detail.forEach((row) => {
        const loc = (row.loc || []).slice(1).join(".");
        if (loc) fieldErrors[loc] = row.msg;
      });
      if (!err.fieldErrors) err.fieldErrors = fieldErrors;
    }
    throw err;
  }
  return data;
}

/* -------------------------------- Tools --------------------------------- */

export async function adminListTools() {
  const res = await fetch(`/api/admin/tools`, { credentials: "include" });
  return handle(res);
}

export async function adminCreateTool(body) {
  const res = await fetch(`/api/admin/tools`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return handle(res);
}

export async function adminUpdateTool(id, patch) {
  const res = await fetch(`/api/admin/tools/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  return handle(res);
}

export async function adminDeleteTool(id) {
  const res = await fetch(`/api/admin/tools/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  return handle(res);
}

export async function adminListQuickPicks() {
  const res = await fetch(`/api/admin/tools/quick-picks`, {
    credentials: "include",
  });
  return handle(res);
}

export async function adminReplaceQuickPicks(picks) {
  const res = await fetch(`/api/admin/tools/quick-picks`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ picks }),
  });
  return handle(res);
}

/* ------------------------------ Articles -------------------------------- */

export async function adminListArticles() {
  const res = await fetch(`/api/admin/articles`, { credentials: "include" });
  return handle(res);
}

export async function adminCreateArticle(body) {
  const res = await fetch(`/api/admin/articles`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return handle(res);
}

export async function adminUpdateArticle(id, patch) {
  const res = await fetch(`/api/admin/articles/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  return handle(res);
}

export async function adminDeleteArticle(id) {
  const res = await fetch(`/api/admin/articles/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  return handle(res);
}
